import { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../utils/axiosInstance';
import GlobalError from '../components/GlobalError';
import { Link as RouterLink } from 'react-router-dom';
import InputFieldAddress from '../components/InputFieldAddress';
import TableView from '../components/TableView';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { formatCurrency } from '../utils/format';
import { calculateTotalAmount, calculateItemSubtotal, calculateSellingPriceIncludingTax } from '../utils/function';
import DefinitionRow from '../components/DefinitionRow';
import {
    Table, TableBody, TableRow, TableCell, TextField, Link, Stack, Divider,
    Box, Radio, RadioGroup, FormControlLabel, Typography, Button, Paper
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
const EcCartCheckoutPage = () => {
    const navigate = useNavigate();
    const [clientData, setClientData] = useState({});
    const [cartItems, setCartItems] = useState([]);
    const { register, handleSubmit, control, formState: { errors }, setValue } = useForm({
        defaultValues: []
    });
    const [error, setError] = useState(null);
    const [isNewAddress, setIsNewAddress] = useState(false);  // 新規住所入力の切り替え
    const cartItemsWithSubtotal = useMemo(() => {
        return cartItems.map(item => ({
            ...item,
            ...calculateItemSubtotal(item), // 既存関数をそのまま使用
        }));
    }, [cartItems]);

    // 合計を計算
    const { total_amount, total_amount_including_tax } = useMemo(() => {
        return calculateTotalAmount(cartItemsWithSubtotal);
    }, [cartItemsWithSubtotal]);

    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    useEffect(() => {
        const fetchClientAndCart = async () => {
            try {
                const [clientRes, cartRes] = await Promise.all([
                    axiosInstance.get('/cart/client'),
                    axiosInstance.get('/cart'),
                ]);

                setClientData(clientRes.data);
                setCartItems(cartRes.data);
            } catch (err) {
                console.error(err);
                setError('カートアイテムまたはお客様情報の取得に失敗しました');
            }
        };

        fetchClientAndCart();
    }, []);
    useEffect(() => {
        // clientData が取得された後に判定
        if (clientData && clientData.address_id) {
            setIsNewAddress(false); // 既存住所
        } else {
            setIsNewAddress(true);  // 新規住所
        }
    }, [clientData]);
    // カラム設定
    const columns = [
        {
            label: '商品',
            accessor: 'image_url',
            render: (item) => {
                return (
                    <img
                        alt={item.product_name || '商品画像'}
                        src={
                            item.image_url
                                ? `${process.env.REACT_APP_IMAGE_BASE_URL}${item.image_url.replace(/\\/g, '/')}`
                                : `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NOIMAGE}`
                        }
                        className="product-image"
                        onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NO_IMAGE}`;
                        }}
                    />

                );
            },
            width: '80px',
        },
        {
            accessor: 'sku',
            render: (item) => (
                <>
                    <Link component={RouterLink} to={`/products/${item.product_id}`} underline="hover">
                        {item.sku}<br />
                        {item.product_name +
                            (item.option_combination && Object.keys(item.option_combination).length > 0
                                ? ' / ' + Object.entries(item.option_combination)
                                    .map(([type, value]) => `(${type}) ${value}`)
                                    .join(' / ')
                                : ''
                            )
                        }
                    </Link>
                    <Typography>
                        {formatCurrency(item.selling_price)}
                        <Typography variant='caption'>（税込{formatCurrency(item.selling_price_including_tax)}）</Typography>
                    </Typography>
                </>
            ),
            minWidth: '200px',
        },
        {
            label: '数量',
            accessor: 'quantity',
            render: (item) => (
                <>{item.quantity}個</>
            ),

            width: '140px',
        },
        {
            label: '金額',
            accessor: 'total_price',
            render: (item) => {
                return <>
                    <Typography>{formatCurrency(item.selling_price * item.quantity)}</Typography>
                    <Typography variant='caption'>税込 {formatCurrency(item.selling_price_including_tax * item.quantity)}</Typography>
                </>;
            },
            width: '200px',
        },

    ];

    // 注文確定の送信
    const onSubmit = async (data) => {
        let addressData = [];

        if (!isNewAddress) {
            addressData = [{
                address_id: clientData.address_id,
                postal_code: clientData.postal_code,
                state: clientData.state,
                address_line: clientData.address_line,
            }];
        } else {
            addressData = [{
                address_id: '',
                postal_code: data.postal_code,
                state: data.state,
                address_line: data.address_line,
            }];
        }

        const formattedDeliveryDate = data.delivery_date === '' ? null : data.delivery_date;
        const orderData = {
            order_items: cartItemsWithSubtotal.map(item => {
                const productNameWithOptions = item.product_name +
                    (item.option_combination && Object.keys(item.option_combination).length > 0
                        ? Object.entries(item.option_combination)
                            .map(([type, value]) => ` / (${type}) ${value}`)
                            .join('')
                        : ''
                    );

                return {

                    cart_id: item.cart_id,
                    cart_item_id: item.cart_item_id,
                    product_id: item.product_id,
                    product_name: productNameWithOptions,
                    variant_id: item.variant_id,
                    sku: item.sku,
                    price: item.price,
                    quantity: item.quantity,
                    selling_price: item.selling_price,
                    selling_price_including_tax: item.selling_price_including_tax,
                    subtotal: item.subtotal,
                    subtotal_including_tax: item.subtotal_including_tax,
                    discount_rate: item.credit_terms,
                    tax_rate: item.tax_rate,
                };
            }),
            address_data: addressData,
            order_date: new Date().toISOString(),
            total_amount: total_amount,
            total_amount_including_tax: total_amount_including_tax,
            recipient_name: data.recipient_name,
            delivery_date: formattedDeliveryDate,
            order_notes: data.order_notes,
        };

        try {
            const response = await axiosInstance.post('/cart/confirm', orderData);
            navigate('/cart/complete', {
                state: { orderId: response.data.order_id }
            });
        } catch (error) {
            console.error('注文の確定に失敗しました', error);
            alert(error.response.data.message || '注文の確定に失敗しました。時間をおいて再度お試しください。');
        }

    };

    // ローディングやエラー時の表示
    if (error) return <GlobalError errorMessage={error} />;

    return (
        <>
            <Typography variant='h1'>注文確認・配送／決済</Typography>
            <Typography component='h2' variant='h3'>ご注文内容</Typography>
            <Paper variant='section'>
                {/* カートアイテム一覧 */}
                <TableView dataset={cartItems} columns={columns} />

                {/* 合計金額 */}
                <Box sx={{ maxWidth: 400, ml: 'auto', mt: 2 }}>
                    <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography fontWeight="bold">合計金額（税抜）</Typography>
                            <Typography fontWeight="bold">{formatCurrency(total_amount)}</Typography>
                        </Stack>
                        <Divider />
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                            <Typography fontWeight="bold">合計金額（税込）</Typography>
                            <Typography fontSize={22} fontWeight="bold">{formatCurrency(total_amount_including_tax)}</Typography>
                        </Stack>
                        <Divider />
                    </Stack>
                </Box>
            </Paper>
            <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                    }
                }}>
                <Typography component='h2' variant='h3'>配送先の指定</Typography>

                {/* 注文備考 */}
                <Paper variant='section'>
                    <DefinitionRow label='納品先住所'>
                        <RadioGroup
                            value={isNewAddress ? 'new' : 'existing'}
                            onChange={(e) => setIsNewAddress(e.target.value === 'new')}
                        >
                            {/* 既存の営業所住所 */}
                            {clientData && (
                                <FormControlLabel
                                    value="existing"
                                    control={<Radio />}
                                    label={clientData ? `〒${clientData.postal_code} ${clientData.state}${clientData.address_line}` : ''}
                                />
                            )}
                            {/* 新しい住所 */}
                            <FormControlLabel
                                value="new"
                                control={<Radio />}
                                label="新しい配送先を入力"
                            />

                            {/* 新規住所の入力フォーム */}
                            {isNewAddress && (
                                <Box mt={1}>
                                    <InputFieldAddress
                                        register={register}
                                        errors={errors}
                                        isEditMode={true}
                                        setValue={setValue}
                                        control={control}
                                    />
                                </Box>
                            )}
                        </RadioGroup>
                    </DefinitionRow>
                    <DefinitionRow label='宛名（受取人名）'>
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="宛名（受取人名）"
                            {...register('recipient_name', {
                                required: '※宛名は必須です',
                                maxLength: { value: 50, message: '50文字以内で入力してください' },
                            })}
                            error={!!errors.recipient_name}
                            helperText={errors.recipient_name?.message}
                            inputProps={{ maxLength: 50 }}
                        />
                    </DefinitionRow>
                    <DefinitionRow label='配送希望日'>
                        <TextField
                            variant="outlined"
                            type="date"
                            {...register('delivery_date', {
                                validate: value => {
                                    if (!value) return true;

                                    const today = new Date();
                                    const localDate = new Date(today.getTime() - today.getTimezoneOffset() * 60000)
                                        .toISOString()
                                        .split('T')[0];

                                    return value >= localDate || '※過去の日付は選択できません';
                                }
                            })}
                            error={!!errors.delivery_date}
                            helperText={errors.delivery_date?.message}
                            inputProps={{
                                min: (() => {
                                    const today = new Date();
                                    return new Date(today.getTime() - today.getTimezoneOffset() * 60000)
                                        .toISOString()
                                        .split('T')[0];
                                })(),
                            }}
                        />
                    </DefinitionRow>
                    <DefinitionRow label='連絡事項'>
                        <TextField
                            variant="outlined"
                            multiline
                            minRows={3}
                            placeholder="ご自由に記入ください"
                            {...register('order_notes', { maxLength: { value: 2000, message: '2000文字以内で入力してください' }, })}
                            inputProps={{ maxLength: 2000 }}
                        />
                    </DefinitionRow>
                    <Typography mt={1}>※配送料金については、ご注文料金に含んでおりません。</Typography>
                </Paper>
                <div className="button-row">
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        endIcon={<ChevronRightIcon />}
                    >
                        注文を確定する
                    </Button>
                </div>
            </form >
        </>
    );
};

export default EcCartCheckoutPage;
